// @ts-strict-ignore
import { Injectable, ViewContainerRef } from '@angular/core';
import {
  ConnectionPositionPair,
  Overlay,
  OverlayConfig,
  OverlayRef,
  PositionStrategy,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { combineLatest, Subject } from 'rxjs';
import { startWith, take } from 'rxjs/operators';

// todo: replace with overlay directive [sc-33842]
@Injectable({
  providedIn: 'root',
})
export class PopoverService {
  private overlayRef: OverlayRef;
  private afterClosed$ = new Subject<any>();
  private onClosed = this.afterClosed$.asObservable();
  private popoverConfig: PopoverConfig;

  constructor(private overlay: Overlay) {}

  open(config: PopoverConfig) {
    this.overlayRef = this.overlay.create(
      this.getOverlayConfig(config.overlayConfig),
    );
    this.popoverConfig = config;

    combineLatest([
      this.overlayRef.backdropClick().pipe(startWith(0)),
      this.overlayRef.keydownEvents().pipe(startWith(0)),
    ]).subscribe(([clickEvent, keyEvent]) =>
      this.closeEvents(clickEvent as MouseEvent, keyEvent as KeyboardEvent),
    );

    this.overlayRef.attach(
      new TemplatePortal(config.contextTemplate, config.viewContainerRef, {
        $implicit: config.data,
        close: this.closePopover,
      }),
    );
    return this.onClosed.pipe(take(1));
  }

  private closeEvents(clickEvent: MouseEvent, keyEvent: KeyboardEvent): void {
    if (keyEvent?.key === 'Escape' || !!clickEvent) this.closePopover(null);
  }

  private getOverlayConfig(overlayConfig: PopoverOverlayConfig): OverlayConfig {
    return new OverlayConfig({
      backdropClass: 'backdrop-color',
      hasBackdrop: true,
      positionStrategy: !!overlayConfig.openEvent
        ? this.positionFromCoordinates(overlayConfig)
        : this.positionFromElement(overlayConfig),
      ...overlayConfig,
    });
  }

  private positionFromCoordinates(overlayConfig: any): PositionStrategy {
    const globalPosition = this.overlay.position().global();
    if (this.popoverConfig.coordinates) {
      const { x, y } = this.popoverConfig.coordinates;
      return globalPosition.left(x + 'px').top(y + 'px');
    } else {
      return globalPosition
        .left(this.getLeftPosition(overlayConfig.openEvent))
        .top(this.getTopPosition(overlayConfig.openEvent));
    }
  }

  private positionFromElement(overlayConfig: any): PositionStrategy {
    return this.overlay
      .position()
      .flexibleConnectedTo(overlayConfig.openPosition)
      .withPositions(
        overlayConfig.positionsPair
          ? overlayConfig.positionsPair
          : defaultPositionPairs,
      )
      .withPush(false);
  }

  private getLeftPosition(event: any): string {
    let position;
    if (window.innerWidth - (270 + event.clientX) < 0) {
      position = event.clientX - 270;
      return position + 'px';
    }

    position = event.clientX;
    return position + 'px';
  }

  private getTopPosition(event: any): string {
    let position;
    if (window.innerHeight - (310 + event.clientY) < 0) {
      position = event.clientY - 310;
      return position + 'px';
    }

    position = event.clientY;
    return position + 'px';
  }

  closePopover = (data: any) => {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.afterClosed$.next(data);
    }
  };
}

export interface PopoverConfig {
  coordinates?: {
    x: number;
    y: number;
  };
  clickTarget?: any;
  contextTemplate: any;
  viewContainerRef: ViewContainerRef;
  data?: any;
  overlayConfig?: PopoverOverlayConfig;
}

export interface PopoverOverlayConfig extends OverlayConfig {
  openPosition: any;
  positionsPair?: ConnectionPositionPair[];
  openEvent?: any;
}

export const defaultPositionPairs: ConnectionPositionPair[] = [
  {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  },
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
  },
  {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  },
  {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
  },
  {
    originX: 'end',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
  },
  {
    originX: 'start',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom',
  },
];

export const datePickerPositionPairs: ConnectionPositionPair[] = [
  ...defaultPositionPairs,
  {
    originX: 'center',
    originY: 'center',
    overlayX: 'center',
    overlayY: 'center',
  },
  {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
  },
];
